import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, LineController, Title, Tooltip, Legend, Filler } from 'chart.js';
import { FiDollarSign, FiShoppingCart, FiPackage, FiTrendingUp, FiBox, FiActivity, FiExternalLink, FiRefreshCw } from 'react-icons/fi';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, LineController, Title, Tooltip, Legend, Filler);

const PLATFORM_LABELS = { amazon: 'Amazon', flipkart: 'Flipkart', meesho: 'Meesho', direct: 'Direct / Manual' };
const PLATFORM_COLORS = { amazon: '#f59e0b', flipkart: '#3b82f6', meesho: '#8b5cf6', direct: '#10b981' };
const STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#06b6d4', processing: '#3b82f6',
  shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444', returned: '#f43f5e',
};
const CHART_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-IN');

const Reports = () => {
  const today = new Date();
  const [from, setFrom] = useState(`${today.getFullYear() - 2}-01-01`);
  const [to, setTo] = useState(today.toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('month');
  const [platform, setPlatform] = useState('');
  const [sales, setSales] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const qs = `from=${from}&to=${to}`;
      const plat = platform ? `&platform=${platform}` : '';
      const [sRes, bRes, mRes] = await Promise.all([
        api.get(`/reports/sales.php?${qs}&group_by=${groupBy}${plat}`),
        api.get(`/reports/best-sellers.php?${qs}${plat}&limit=10`),
        api.get(`/reports/marketplace-comparison.php?${qs}`),
      ]);
      if (sRes.data.success) setSales(sRes.data.data);
      else setError(sRes.data.message);
      if (bRes.data.success) setBestSellers(bRes.data.data.products || []);
      if (mRes.data.success) setComparison(mRes.data.data.platforms || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy, platform]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  if (loading && !sales) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading reports...</div>;

  const s = sales?.summary || {};
  const trend = sales?.trend || [];
  const statuses = sales?.status_breakdown || [];
  const categories = sales?.categories || [];

  const kpis = [
    { label: 'Gross Sales', value: money(s.gross_sales), icon: FiDollarSign, color: 'bg-blue-500' },
    { label: 'Net Revenue', value: money(s.net_revenue), icon: FiTrendingUp, color: 'bg-emerald-500' },
    { label: 'Orders', value: num(s.order_count), icon: FiShoppingCart, color: 'bg-violet-500' },
    { label: 'Units Sold', value: num(s.units_sold), icon: FiPackage, color: 'bg-cyan-500' },
    { label: 'Avg. Order Value', value: money(s.aov), icon: FiBox, color: 'bg-amber-500' },
    {
      label: `Net Profit (${s.profit_margin || 0}%)`,
      value: money(s.net_profit),
      icon: FiActivity,
      color: (s.net_profit || 0) >= 0 ? 'bg-emerald-600' : 'bg-red-500',
    },
  ];

  const trendData = {
    labels: trend.length ? trend.map(t => t.period) : ['No Data'],
    datasets: [
      {
        type: 'bar',
        label: 'Sales (₹)',
        data: trend.map(t => t.sales || 0),
        backgroundColor: 'rgba(59, 130, 246, 0.65)',
        borderColor: '#3b82f6',
        borderWidth: 1,
        yAxisID: 'y',
        order: 2,
      },
      {
        type: 'line',
        label: 'Net Profit (₹)',
        data: trend.map(t => t.net_profit || 0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        yAxisID: 'y',
        order: 1,
      },
      {
        type: 'line',
        label: 'Orders',
        data: trend.map(t => t.order_count || 0),
        borderColor: '#8b5cf6',
        backgroundColor: 'transparent',
        borderDash: [5, 4],
        tension: 0.35,
        pointRadius: 3,
        yAxisID: 'y1',
        order: 0,
      },
    ],
  };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { ticks: { maxRotation: 45, minRotation: 0, autoSkip: true } },
      y: { position: 'left', ticks: { callback: v => `₹${v}` } },
      y1: { position: 'right', grid: { drawOnChartArea: false }, ticks: { precision: 0 } },
    },
    plugins: { legend: { position: 'bottom' } },
  };

  const platformData = {
    labels: comparison.map(c => PLATFORM_LABELS[c.platform] || c.platform),
    datasets: [{
      data: comparison.map(c => c.gross_sales || 0),
      backgroundColor: comparison.map(c => PLATFORM_COLORS[c.platform] || '#64748b'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  const statusData = {
    labels: statuses.map(st => st.status),
    datasets: [{
      data: statuses.map(st => st.order_count),
      backgroundColor: statuses.map(st => STATUS_COLORS[st.status] || '#94a3b8'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  const topCategories = categories.slice(0, 6);
  const categoryData = {
    labels: topCategories.map(c => c.category),
    datasets: [{
      label: 'Sales (₹)',
      data: topCategories.map(c => c.sales || 0),
      backgroundColor: CHART_COLORS.map(c => `${c}cc`),
      borderColor: CHART_COLORS,
      borderWidth: 1,
    }],
  };

  const maxUnits = Math.max(1, ...bestSellers.map(b => b.units || 0));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Reports &amp; Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Comprehensive reporting module - Sales reports, profit &amp; loss, best sellers, and marketplace comparisons.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchReports} className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <Link to="/profit-loss" className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">
            <FiExternalLink className="w-4 h-4" /> P&amp;L Analysis
          </Link>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Group By</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white">
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Marketplace</label>
          <select value={platform} onChange={e => setPlatform(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white">
            <option value="">All Platforms</option>
            <option value="amazon">Amazon</option>
            <option value="flipkart">Flipkart</option>
            <option value="meesho">Meesho</option>
          </select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{k.label}</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white">{k.value}</p>
              </div>
              <div className={`${k.color} p-2 rounded-lg`}>
                <k.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sales trend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Sales Trend</h3>
          <span className="text-xs text-gray-500">COGS {money(s.total_cogs)} &middot; Fees {money(s.marketplace_fees)} &middot; Refunds {money(s.refunds)}</span>
        </div>
        <div style={{ height: '340px' }}>
          {trend.length ? <Bar data={trendData} options={trendOptions} /> : <p className="text-center text-gray-500 py-16">No sales data for this period.</p>}
        </div>
      </div>

      {/* Best sellers + platform */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-800 dark:text-white">Best Sellers</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {bestSellers.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No product sales in this period.</td></tr>
                ) : bestSellers.map((b, i) => (
                  <tr key={b.product_id || i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2.5 text-sm text-gray-500">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{b.name}</p>
                      <p className="text-xs text-gray-500">{b.sku}{b.category ? ` · ${b.category.trim()}` : ''}</p>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <span className="w-8">{b.units}</span>
                        <span className="h-2 bg-primary-500 rounded" style={{ width: `${Math.max(6, (b.units / maxUnits) * 80)}px` }} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-sm font-semibold text-right text-gray-900 dark:text-white">{money(b.revenue)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-emerald-600 dark:text-emerald-400">{money(b.gross_profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Sales by Platform</h3>
          <div style={{ height: '240px' }}>
            {comparison.length ? <Doughnut data={platformData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} /> : <p className="text-center text-gray-500 py-16">No data</p>}
          </div>
        </div>
      </div>

      {/* Marketplace comparison */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-white">Marketplace Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross Sales</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Net Revenue</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fees</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">AOV</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Share</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Net Profit</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" title="Active listings / total mapped">Active / Mapped</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {comparison.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500">No marketplace data for this period.</td></tr>
              ) : comparison.map(c => (
                <tr key={c.platform} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[c.platform] || '#64748b' }} />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{PLATFORM_LABELS[c.platform] || c.platform}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{num(c.order_count)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{num(c.units_sold)}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-semibold text-gray-900 dark:text-white">{money(c.gross_sales)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{money(c.net_revenue)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{money(c.marketplace_fees)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{money(c.aov)}</td>
                  <td className="px-4 py-2.5 text-sm text-right">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{c.sales_share}%</span>
                  </td>
                  <td className={`px-4 py-2.5 text-sm text-right font-semibold ${(c.net_profit || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600'}`}>{money(c.net_profit)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">{num(c.active_listings)} / {num(c.listed_products)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Categories + status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Sales by Category</h3>
          <div style={{ height: '280px' }}>
            {categories.length ? <Bar data={categoryData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { ticks: { callback: v => `₹${v}` } } } }} /> : <p className="text-center text-gray-500 py-16">No category data for this period.</p>}
          </div>
          {categories.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sales</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {categories.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{c.category}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{num(c.order_count)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{num(c.units_sold)}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-white">{money(c.sales)}</td>
                      <td className="px-3 py-2 text-sm text-right text-emerald-600 dark:text-emerald-400">{money(c.gross_profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Order Status</h3>
          <div style={{ height: '220px' }}>
            {statuses.length ? <Doughnut data={statusData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} /> : <p className="text-center text-gray-500 py-16">No data</p>}
          </div>
          <div className="mt-4 space-y-2">
            {statuses.map((st, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[st.status] || '#94a3b8' }} />
                  <span className="capitalize">{st.status}</span>
                </span>
                <span className="text-gray-800 dark:text-white font-medium">{st.order_count} &middot; {money(st.sales)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <div className="flex justify-between"><span>Returns</span><span>{num(s.return_count)}</span></div>
            <div className="flex justify-between"><span>Refunds</span><span>{money(s.refunds)}</span></div>
            <div className="flex justify-between"><span>Other Expenses</span><span>{money(s.other_expenses)}</span></div>
            <div className="flex justify-between"><span>Collected GST</span><span>{money(s.tax_collected)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
